import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { MAIN_THEME } from '@styles';
import { getError } from '@utils/graph-utils';
import { cloneDeep, isEmpty } from 'lodash';
import { OpenConfirmationAction } from 'src/core/redux/confirmation/actions';
import { IConfirmation } from 'src/core/redux/confirmation/types';
import { useAppDispatch } from 'src/core/redux/hooks';
import { makeStyles } from 'tss-react/mui';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';
import { IBaseDrawerProps } from '@common/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import {
  useActivateBusinessPartnerLines,
  useDeactivateBusinessPartnerLines,
  useGetActiveBusinessPartnerLines,
  useGetInactiveBusinessPartnerLines
} from '@common/slices';

const initialValues = {
  activateLines: {
    lineIDs: [] as string[]
  }
};

function toLookups(data: any, isDeactivate: boolean): Record<string, string> {
  const lookup: Record<string, string> = {};

  const lines = isDeactivate
    ? (data?.salesforce?.getActiveBusinessPartnerLines ?? [])
    : (data?.salesforce?.getInActiveBusinessPartnerLines ?? []);

  lines
    .slice()
    .sort((a: any, b: any) => {
      const codeA = Number(a.line?.code) || 0;
      const codeB = Number(b.line?.code) || 0;

      return codeA - codeB;
    })
    .forEach((l: any) => {
      lookup[l.id] = `${l.line.code} - ${l.line.name}`;
    });

  return lookup;
}

const useStyles = makeStyles()(() => ({
  note: {
    color: 'red !important',
    fontSize: `${MAIN_THEME.fontSize.xs} !important`,
    fontStyle: 'italic'
  }
}));

interface Props extends IBaseDrawerProps {
  businessPartnerId: string;
  isDeactivate?: boolean;
}

const ActivateLinesAndPlansDrawer: React.FC<Props> = ({
  onSuccess,
  businessPartnerId,
  isDeactivate = false
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>([
    {
      inputs: {
        lineIDs: {
          name: 'lineIDs',
          title: 'Line',
          type: FormFieldType.multiSelect,
          placeholder: 'Select inactive line(s)',
          value: initialValues.activateLines.lineIDs,
          selectOptions: {},
          required: true
        }
      }
    }
  ]);

  const { data: inactiveLinesData, loading: inactiveLinesLoading } =
    useGetInactiveBusinessPartnerLines.useQuery({
      options: {
        variables: { businessPartnerId },
        skip: isDeactivate
      }
    });

  const { data: activeLinesData, loading: activeLinesLoading } =
    useGetActiveBusinessPartnerLines.useQuery({
      options: {
        variables: { businessPartnerId },
        skip: !isDeactivate
      }
    });

  const [activateLines] = useActivateBusinessPartnerLines();
  const [deactivateLines] = useDeactivateBusinessPartnerLines();

  useEffect(() => {
    if (!isDeactivate) {
      if (!inactiveLinesLoading && inactiveLinesData) {
        const updatedSections = cloneDeep(inputsForm);
        const options = toLookups(inactiveLinesData, isDeactivate);
        (updatedSections[0].inputs.lineIDs as any).selectOptions = options;
        setInputsForm(updatedSections);
      }
    } else {
      if (!activeLinesLoading && activeLinesData) {
        const updatedSections = cloneDeep(inputsForm);
        const options = toLookups(activeLinesData, isDeactivate);
        (updatedSections[0].inputs.lineIDs as any).selectOptions = options;
        setInputsForm(updatedSections);
      }
    }
  }, [
    inactiveLinesLoading,
    inactiveLinesData,
    activeLinesLoading,
    activeLinesData,
    isDeactivate
  ]);

  const submitForm = (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      [
        {
          inputs: {
            lineIDs: {
              name: 'lineIDs',
              title: 'Line',
              type: FormFieldType.multiSelect,
              placeholder: 'Select inactive line(s)',
              value: initialValues.activateLines.lineIDs,
              selectOptions: {},
              required: true
            }
          }
        }
      ],
      values
    );
    const selectedLineIds: string[] = data.lineIDs || [];

    if (!selectedLineIds.length) {
      toast.error('Please select at least one line.');

      return;
    }

    const confirmation: IConfirmation = {
      open: true,
      title: `${isDeactivate ? 'Deactivate' : 'Activate'} Line(s) & Plans`,
      message: `${isDeactivate ? 'Are you sure you want to deactivate the selected Lines and corresponding plans?' : 'Are you sure you want to activate the selected Lines and corresponding plans?'}`,
      submitButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      callback: async () => {
        try {
          let res;
          if (isDeactivate) {
            res = await deactivateLines({
              businessPartnerId,
              businesspartnerLines: selectedLineIds
            });
          } else {
            res = await activateLines({
              businessPartnerId,
              businesspartnerLines: selectedLineIds
            });
          }

          if (isEmpty(res.errors)) {
            onSuccess();
          }
        } catch (err) {
          toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
        }
      }
    };

    dispatch(OpenConfirmationAction(confirmation));
  };

  return (
    <DynamicFormWidget
      sections={inputsForm}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      noteSection={{
        className: classes.note,
        text: `Note that ${isDeactivate ? 'deactivating' : 'activating'} the selected lines will ${isDeactivate ? 'deactivate' : 'activate'} all related assigned plans.`
      }}
    />
  );
};

export { ActivateLinesAndPlansDrawer };
